import { Formik } from "formik";
import { useMemo, useState } from "react";
import { connect } from "react-redux";
import {
  Box,
  Container,
  Grid,
  IconButton,
  makeStyles,
  Tab,
  Tabs,
  Tooltip,
} from "@material-ui/core";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import { MdAdd } from "react-icons/md";
import DataTable, { TableColumn } from "react-data-table-component";

import { tableCustomStyles } from "../../../utils/table-styles";

import { history } from "../../..";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
  clearTransportCompanyContact,
  clearTransportCompanyTruck,
  getTransportCompany,
  getTransportCompanyContacts,
  getTransportCompanyTrucks,
  setTransportCompany,
  setTransportCompanyContacts,
  setTransportCompanyTrucks,
} from "../../../store/transportStore";
import transportCompanyService from "../../../services/transportCompanyService";
import { TransportCompany } from "../../../interface/api/TransportCompany";
import { SetTransportCompanyAction } from "../../../interface/redux-types/SetTransportCompanyAction";
import { TransportCompanyFormValues } from "../../../interface/formik/TransportCompanyFormValues";
import { TransportCompanyFormSchema } from "../schema/transport-company-form-schema";
import PageHero from "../../shared/page-hero";
import FormErrorMessage from "../../shared/form-error-message";
import FormTextField from "../../shared/form-text-field";
import Button from "../../shared/Button";
import { TransportContact } from "../../../interface/api/TransportContact";
import DeleteDialog from "../../shared/dialog/delete";
import { ActionIconGroup, EditIcon, DeleteIcon } from "../../shared/icons";
import { SetTransportContactsAction } from "../../../interface/redux-types/SetTransportContactsAction";
import transportContactService from "../../../services/transportContactService";
import { Truck } from "../../../interface/api/Truck";
import { SetTrucksAction } from "../../../interface/redux-types/SetTrucksAction";
import truckService from "../../../services/truckService";
import { TabPanel } from "../../shared/tab-panel";
import { a11yProps } from "../../../utils/constants";
import PaginatedTableTop, {
  paginationTopClassName,
} from "../../shared/paginated-table-top";

const useStyles = makeStyles({
  tableContainer: {
    marginTop: "2rem",
  },
  addIconButton: {
    border: "1px solid rgba(0, 0, 0, 0.23)",
  },
});

const TransportCompanyView = ({}) => {
  const classes = useStyles();
  const { id }: any = useParams();
  const dispatch = useAppDispatch();
  const transportCompany = useAppSelector(getTransportCompany);
  const transportContacts = useAppSelector(getTransportCompanyContacts);
  const trucks = useAppSelector(getTransportCompanyTrucks);
  const [openTransportContactDialog, setOpenTransportContactDialog] =
    useState(false);
  const [selectedTransportContactItem, setSelectedTransportContactItem] =
    useState<Partial<TransportContact>>({});
  const [openTransportTruckDialog, setOpenTransportTruckDialog] =
    useState(false);
  const [selectedTransportTruckItem, setSelectedTransportTruckItem] = useState<
    Partial<Truck>
  >({});
  const [tab, setTab] = useState(0);

  const onAddTruck = () => {
    dispatch(clearTransportCompanyTruck());
    history.push(`/trucks/new/transport-company/${id}`);
  };

  const onAddContact = () => {
    dispatch(clearTransportCompanyContact());
    history.push(`/contacts/new/transport-company/${id}`);
  };
  const onOpenTransportTruckDialog = () => setOpenTransportTruckDialog(true);
  const onCloseTransportTruckDialog = () => setOpenTransportTruckDialog(false);

  const onOpenTransportContactDialog = () =>
    setOpenTransportContactDialog(true);

  const onCloseTransportContactDialog = () =>
    setOpenTransportContactDialog(false);

  useMemo(() => {
    if (!id || id === "new") return;

    transportCompanyService.getTransportCompany(id).then((response) => {
      if (response.ok) {
        const transportCompany = response.data as TransportCompany;
        const setTransportCompanyAction: SetTransportCompanyAction = {
          transportCompany,
        };

        dispatch(setTransportCompany(setTransportCompanyAction));
      } else {
        toast.error(response.originalError.message);
      }
    });

    transportContactService
      .getTransportContacts({
        transportCompanyId: id,
        truck: null,
        transportContact: null,
      })
      .then((response) => {
        if (response.ok) {
          const transportContacts = response.data as TransportContact[];
          const setTransportContactsAction: SetTransportContactsAction = {
            transportContacts: transportContacts,
          };
          dispatch(setTransportCompanyContacts(setTransportContactsAction));
        }
      });

    truckService
      .getTrucks({
        transportCompanyId: id,
        truck: null,
        transportContact: null,
      })
      .then((response) => {
        if (response.ok) {
          const trucks = response.data as Truck[];
          const setTrucksAction: SetTrucksAction = {
            trucks,
          };
          dispatch(setTransportCompanyTrucks(setTrucksAction));
        }
      });
  }, []);

  const onSubmit = (
    values: TransportCompanyFormValues,
    setSubmitting: Function
  ) => {
    transportCompanyService
      .saveTransportCompany(values as TransportCompany)
      .then((response) => {
        if (response.ok) {
          history.push("/transports");
        } else {
          const data = response.data as string;
          toast.error(data);
          setSubmitting(false);
        }
      });
  };

  const onConfirmDeleteTransportContact = () => {
    if (selectedTransportContactItem && selectedTransportContactItem.id) {
      transportContactService
        .deleteTransportContact(selectedTransportContactItem.id)
        .then((response) => {
          if (response.ok) {
            const setTransportContactsAction: SetTransportContactsAction = {
              transportContacts: transportContacts.filter(
                (c) => c.id !== selectedTransportContactItem.id
              ),
            };
            dispatch(setTransportCompanyContacts(setTransportContactsAction));
          }
        });
    }

    onCloseTransportContactDialog();
  };

  const onConfirmDeleteTransportTruck = () => {
    if (selectedTransportTruckItem && selectedTransportTruckItem.id) {
      truckService
        .deleteTruck(selectedTransportTruckItem.id)
        .then((response) => {
          if (response.ok) {
            const setTrucksAction: SetTrucksAction = {
              trucks: trucks.filter(
                (t) => t.id !== selectedTransportTruckItem.id
              ),
            };
            dispatch(setTransportCompanyTrucks(setTrucksAction));
          }
        });
    }

    onCloseTransportTruckDialog();
  };

  const initialValues: TransportCompanyFormValues = transportCompany
    ? transportCompany
    : {
        id: 0,
        name: "",
      };

  const truckColumns: TableColumn<Truck>[] = [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: true,
      width: "80px",
    },
    {
      name: "Driver",
      selector: (row) => row.driverName,
    },
    {
      name: "Phone Number",
      selector: (row) => row.driverNumber,
    },
    {
      name: "Truck Number",
      selector: (row) => row.number,
    },
    {
      name: "License Plate",
      selector: (row) => row.licensePlate,
    },
    {
      name: "Tare Weight",
      selector: (row) => row.tareWeight,
    },
    {
      name: "Actions",
      cell: (row) => (
        <ActionIconGroup spread>
          <EditIcon
            onClick={() =>
              history.push(`/trucks/${row.id}/transport-company/${id}`)
            }
          />
          <DeleteIcon
            onClick={() => {
              setSelectedTransportTruckItem(row);
              onOpenTransportTruckDialog();
            }}
          />
        </ActionIconGroup>
      ),
    },
  ];

  const contactColumns: TableColumn<TransportContact>[] = [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: true,
      width: "80px",
    },
    {
      name: "Name",
      selector: (row) => row.name,
    },
    {
      name: "Number",
      selector: (row) => row.number,
    },
    {
      name: "Actions",
      cell: (row) => (
        <ActionIconGroup spread>
          <EditIcon
            onClick={() =>
              history.push(`/contacts/${row.id}/transport-company/${id}`)
            }
          />
          <DeleteIcon
            onClick={() => {
              setSelectedTransportContactItem(row);
              onOpenTransportContactDialog();
            }}
          />
        </ActionIconGroup>
      ),
    },
  ];

  return (
    <Container maxWidth="xl">
      <PageHero
        title={
          id > 0
            ? `${transportCompany ? transportCompany.name : id}`
            : "New Transport Company"
        }
        subtitle={
          id > 0 ? "Edit Transport Company." : "Add a new Transport Company."
        }
        showBackButton={true}
      />
      <Formik
        initialValues={initialValues}
        validationSchema={TransportCompanyFormSchema}
        onSubmit={(values, { setSubmitting }) =>
          onSubmit(values, setSubmitting)
        }
        enableReinitialize={true}
      >
        {({ values, handleSubmit, isSubmitting }) => (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md>
                <FormTextField
                  name="name"
                  value={values.name}
                  label={"Company Name"}
                />
                <FormErrorMessage name="name" />
              </Grid>
              <Grid item>
                <Button
                  label="Save"
                  color="primary"
                  variant="contained"
                  type="submit"
                  disabled={isSubmitting}
                />
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
      <div className={classes.tableContainer}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Tabs
            value={tab}
            onChange={(_e, newValue) => setTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Trucks" {...a11yProps(0)} />
            <Tab label="Contacts" {...a11yProps(1)} />
          </Tabs>
          <Tooltip title={tab === 0 ? "Add Truck" : "Add Contact"}>
            <IconButton
              color="primary"
              className={classes.addIconButton}
              onClick={() => (tab === 0 ? onAddTruck() : onAddContact())}
            >
              <MdAdd size={24} />
            </IconButton>
          </Tooltip>
        </Box>
        <TabPanel value={tab} index={0}>
          <Box mt={2}>
            <PaginatedTableTop>
              <DataTable
                className={paginationTopClassName}
                columns={truckColumns}
                data={trucks ?? []}
                customStyles={tableCustomStyles}
                striped
                highlightOnHover
                responsive
                pagination
                paginationPerPage={25}
                paginationRowsPerPageOptions={[25, 50, 100, 200]}
              />
            </PaginatedTableTop>
          </Box>
        </TabPanel>
        <TabPanel value={tab} index={1}>
          <Box mt={2}>
            <PaginatedTableTop>
              <DataTable
                className={paginationTopClassName}
                columns={contactColumns}
                data={transportContacts ?? []}
                customStyles={tableCustomStyles}
                striped
                highlightOnHover
                responsive
                pagination
                paginationPerPage={25}
                paginationRowsPerPageOptions={[25, 50, 100, 200]}
              />
            </PaginatedTableTop>
          </Box>
        </TabPanel>
      </div>
      <DeleteDialog
        open={openTransportTruckDialog}
        onClose={onCloseTransportTruckDialog}
        onConfirmDelete={onConfirmDeleteTransportTruck}
      />
      <DeleteDialog
        open={openTransportContactDialog}
        onClose={onCloseTransportContactDialog}
        onConfirmDelete={onConfirmDeleteTransportContact}
      />
    </Container>
  );
};

export default connect()(TransportCompanyView);
